
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bug } from "lucide-react";
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface RootCauseAnalysisProps {
  incidents: any[];
}

export function RootCauseAnalysis({ incidents }: RootCauseAnalysisProps) {
  const rootCauseData = React.useMemo(() => {
    // Extract common keywords/phrases that might indicate root causes
    const rootCauseKeywords = {
      "API Error": ["api", "endpoint", "response", "request", "http", "rest"],
      "Database Issue": ["database", "db", "query", "sql", "timeout", "connection"],
      "Network Problem": ["network", "connectivity", "timeout", "dns", "connection"],
      "Configuration": ["config", "setting", "parameter", "environment", "variable"],
      "Authorization": ["auth", "permission", "access", "denied", "unauthorized", "token"],
      "Front-end Issue": ["ui", "interface", "screen", "display", "browser", "rendering"],
      "Performance": ["slow", "performance", "latency", "response time", "memory", "cpu"],
      "3rd Party System": ["third party", "3rd party", "vendor", "external", "integration"],
      "Data Error": ["data", "corrupt", "invalid", "missing", "incorrect"]
    };
    
    // Count incidents by root cause
    const causeCounts: Record<string, number> = {};
    
    incidents.forEach(incident => {
      const description = (incident.short_description || incident.description || "").toLowerCase();
      
      // Find matching root cause based on keywords
      let matched = false;
      
      for (const [cause, keywords] of Object.entries(rootCauseKeywords)) {
        for (const keyword of keywords) {
          if (description.includes(keyword)) {
            causeCounts[cause] = (causeCounts[cause] || 0) + 1;
            matched = true;
            break;
          }
        }
        
        if (matched) break;
      }
      
      // If no specific cause found, count as "Other"
      if (!matched) {
        causeCounts["Other"] = (causeCounts["Other"] || 0) + 1;
      }
    });
    
    // Convert to array format for the chart
    return Object.entries(causeCounts)
      .map(([cause, count]) => ({ cause, count }))
      .sort((a, b) => b.count - a.count);
  }, [incidents]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="h-5 w-5" />
          Root Cause Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rootCauseData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="cause" width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Incident Count" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
